import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, AlertTriangle, Package, DollarSign, Calendar, Send, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, money, fmtDate, statusPill, statusLabel } from '../../lib/api';
import PullToRefresh from '../../components/PullToRefresh';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export default function AdminDashboard() {
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = async () => {
    setRefreshKey((k) => k + 1);
    // small delay so the spinner is visible while child blocks refetch
    await new Promise((r) => setTimeout(r, 400));
  };
  return (
    <PullToRefresh onRefresh={refresh}>
    <div className="fade-in space-y-5" data-testid="admin-dashboard" key={refreshKey}>
      <div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-[#6b7280] mt-1">Your business at a glance</p>
      </div>
      <FinancialOverview />
      <PastDueAlerts />
      <InventoryValueCard />
      <InventoryAlerts />
    </div>
    </PullToRefresh>
  );
}

/* ============================================================
   Block 1 — Financial Overview (collapsible)
   ============================================================ */
function FinancialOverview() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [invoicesOpen, setInvoicesOpen] = useState(false);
  const [available, setAvailable] = useState([]);
  const [period, setPeriod] = useState(null); // null = All time
  const [stats, setStats] = useState(null);

  useEffect(() => {
    adminApi.get('/admin/dashboard/available-months').then((r) => setAvailable(r.data || []));
  }, []);

  useEffect(() => {
    const params = {};
    if (period) {
      params.year = period.year;
      params.month = period.month;
    }
    adminApi.get('/admin/dashboard/period', { params }).then((r) => setStats(r.data));
    setInvoicesOpen(false); // collapse the invoice list when switching period
  }, [period]);

  const periodValue = period ? `${period.year}-${period.month}` : 'all';
  const periodLabel = period ? `${MONTH_NAMES[period.month - 1]} ${period.year}` : 'All time';

  const onChangePeriod = (e) => {
    e.stopPropagation();
    const v = e.target.value;
    if (v === 'all') setPeriod(null);
    else {
      const [y, m] = v.split('-').map(Number);
      setPeriod({ year: y, month: m });
    }
  };

  if (!stats) return <div className="card p-6 text-sm text-[#6b7280]">Loading…</div>;

  const totalUnits = (stats.sold_items || []).reduce((s, i) => s + i.qty, 0);

  return (
    <div className="card overflow-hidden" data-testid="financial-overview-block">
      {/* Header — always visible, click to toggle */}
      <div
        onClick={() => setExpanded(!expanded)}
        className="p-5 sm:p-6 cursor-pointer hover:bg-[#fafafa] transition-colors"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-[#f3f4f6] flex items-center justify-center">
              <DollarSign size={18} className="text-[#0a0a0a]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Financial Overview</h2>
              <p className="text-xs text-[#6b7280]">{periodLabel} · {expanded ? 'click to collapse' : 'click for full breakdown'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b7280] pointer-events-none" />
              <select
                value={periodValue}
                onChange={onChangePeriod}
                className="pl-9 pr-8 py-2 text-sm border border-[#e7e7e7] rounded-lg bg-white font-medium min-w-[170px]"
                data-testid="dashboard-month-select"
              >
                <option value="all">All time</option>
                {available.map((m) => (
                  <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                    {MONTH_NAMES[m.month - 1]} {m.year}
                  </option>
                ))}
              </select>
            </div>
            {expanded ? <ChevronUp size={20} className="text-[#6b7280]" /> : <ChevronDown size={20} className="text-[#6b7280]" />}
          </div>
        </div>

        {/* Always-visible main metrics — stack on mobile so big $ amounts don't truncate */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <BigStat label="Revenue" value={money(stats.revenue)} />
          <BigStat label="Paid" value={money(stats.paid)} tint="green" />
          <BigStat label="Unpaid" value={money(stats.unpaid)} tint="red" />
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-[#f3f3f3] bg-[#fafafa] p-5 sm:p-6 space-y-5" data-testid="financial-detail">
          {/* Order: Invoices → Profit → Paid Profit → Unpaid Profit (matches Revenue/Paid/Unpaid above) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <ClickableStat
              label="Invoices"
              value={stats.invoices_count}
              active={invoicesOpen}
              onClick={() => setInvoicesOpen(!invoicesOpen)}
              testid="stat-invoices-toggle"
            />
            <SmallStat label="Profit" value={money(stats.profit)} tint="blue" />
            <SmallStat label="Paid Profit" value={money(stats.paid_profit)} tint="green" />
            <SmallStat label="Unpaid Profit" value={money(stats.unpaid_profit)} tint="red" />
          </div>

          {/* Drill-down: list of invoices in this period */}
          {invoicesOpen && (
            <div className="bg-white border border-[#eaeaea] rounded-xl overflow-hidden" data-testid="period-invoices">
              <div className="p-4 border-b border-[#f3f3f3] flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  Invoices in {periodLabel}
                </h3>
                <span className="text-xs text-[#6b7280]">{stats.invoices?.length || 0} invoices · sorted newest first</span>
              </div>
              {!stats.invoices || stats.invoices.length === 0 ? (
                <div className="p-6 text-center text-sm text-[#6b7280]">No invoices in this period.</div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-sm">
                      <thead className="bg-[#fafafa] text-[#6b7280] text-[11px] uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-4 py-2">Invoice #</th>
                          <th className="text-left px-4 py-2">Customer</th>
                          <th className="text-left px-4 py-2">Issued</th>
                          <th className="text-left px-4 py-2">Due</th>
                          <th className="text-left px-4 py-2">Status</th>
                          <th className="text-right px-4 py-2">Total</th>
                          <th className="text-right px-4 py-2">Paid</th>
                          <th className="text-right px-4 py-2">Balance</th>
                          <th className="px-2 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.invoices.map((inv) => {
                          const balance = (inv.total || 0) - (inv.paid_amount || 0);
                          return (
                            <tr
                              key={inv.invoice_id}
                              className="border-t border-[#f3f3f3] hover:bg-[#fff5f5] cursor-pointer"
                              onClick={() => navigate(`/admin/orders/${inv.invoice_id}`)}
                              data-testid={`period-inv-row-${inv.invoice_id}`}
                            >
                              <td className="px-4 py-2 mono font-semibold text-[#d92027]">{inv.invoice_number}</td>
                              <td className="px-4 py-2">
                                <div className="font-medium text-[13px]">{inv.customer_name}</div>
                                <div className="text-xs text-[#6b7280]">{inv.customer_email}</div>
                              </td>
                              <td className="px-4 py-2 text-xs">{fmtDate(inv.issue_date)}</td>
                              <td className="px-4 py-2 text-xs">{fmtDate(inv.due_date)}</td>
                              <td className="px-4 py-2"><StatusPill status={inv.status} /></td>
                              <td className="text-right px-4 py-2 mono">{money(inv.total)}</td>
                              <td className="text-right px-4 py-2 mono text-green-700">{money(inv.paid_amount)}</td>
                              <td className={`text-right px-4 py-2 mono font-semibold ${balance > 0 ? 'text-[#d92027]' : 'text-green-700'}`}>{money(balance)}</td>
                              <td className="text-right px-2 py-2">
                                <ExternalLink size={14} className="inline text-[#9ca3af]" />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="md:hidden divide-y divide-[#f3f3f3]">
                    {stats.invoices.map((inv) => {
                      const balance = (inv.total || 0) - (inv.paid_amount || 0);
                      return (
                        <div
                          key={inv.invoice_id}
                          onClick={() => navigate(`/admin/orders/${inv.invoice_id}`)}
                          className="p-3 hover:bg-[#fff5f5]"
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="mono font-semibold text-[#d92027] truncate">{inv.invoice_number}</div>
                            <StatusPill status={inv.status} />
                          </div>
                          <div className="text-sm truncate">{inv.customer_name}</div>
                          <div className="text-xs text-[#6b7280]">Issued {fmtDate(inv.issue_date)} · Due {fmtDate(inv.due_date)}</div>
                          <div className="grid grid-cols-3 gap-2 text-xs mt-2 pt-2 border-t border-[#f3f3f3]">
                            <div>
                              <div className="text-[10px] uppercase text-[#9ca3af]">Total</div>
                              <div className="mono font-semibold">{money(inv.total)}</div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase text-[#9ca3af]">Paid</div>
                              <div className="mono font-semibold text-green-700">{money(inv.paid_amount)}</div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase text-[#9ca3af]">Balance</div>
                              <div className={`mono font-semibold ${balance > 0 ? 'text-[#d92027]' : 'text-green-700'}`}>{money(balance)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Sold items breakdown — Revenue & Profit each split into paid vs unpaid */}
          <div className="bg-white border border-[#eaeaea] rounded-xl overflow-hidden">
            <div className="p-4 border-b border-[#f3f3f3] flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Package size={16} /> Sold Items Breakdown
              </h3>
              <span className="text-xs text-[#6b7280]">
                {stats.sold_items?.length || 0} products · {totalUnits} total units · paid <span className="text-green-700 font-semibold">vs</span> unpaid
              </span>
            </div>
            {stats.sold_items?.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#6b7280]">No items sold in this period.</div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="overflow-x-auto hidden md:block">
                  <table className="w-full text-sm">
                    <thead className="bg-[#fafafa] text-[#6b7280] text-[11px] uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-4 py-2">Product</th>
                        <th className="text-right px-4 py-2">Units</th>
                        <th className="text-right px-4 py-2">
                          Revenue
                          <div className="text-[9px] normal-case font-normal text-[#9ca3af]">
                            <span className="text-green-700">paid</span> <span className="text-[#9ca3af]">vs</span> <span className="text-[#d92027]">unpaid</span>
                          </div>
                        </th>
                        <th className="text-right px-4 py-2">
                          Profit
                          <div className="text-[9px] normal-case font-normal text-[#9ca3af]">
                            <span className="text-green-700">paid</span> <span className="text-[#9ca3af]">vs</span> <span className="text-[#d92027]">unpaid</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.sold_items.map((it, idx) => (
                        <tr key={idx} className="border-t border-[#f3f3f3]">
                          <td className="px-4 py-3 font-medium align-top">{it.name}</td>
                          <td className="text-right px-4 py-3 mono font-bold align-top">{it.qty}</td>
                          <td className="text-right px-4 py-3 mono align-top">
                            <div className="font-semibold">{money(it.revenue)}</div>
                            <div className="text-[11px] mt-0.5">
                              <span className="text-green-700">{money(it.paid_revenue)}</span>
                              <span className="text-[#9ca3af] mx-1">/</span>
                              <span className="text-[#d92027]">{money(it.unpaid_revenue)}</span>
                            </div>
                          </td>
                          <td className={`text-right px-4 py-3 mono align-top ${it.profit >= 0 ? '' : 'text-[#d92027]'}`}>
                            <div className="font-semibold">{money(it.profit)}</div>
                            <div className="text-[11px] mt-0.5">
                              <span className="text-green-700">{money(it.paid_profit)}</span>
                              <span className="text-[#9ca3af] mx-1">/</span>
                              <span className="text-[#d92027]">{money(it.unpaid_profit)}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-[#f3f3f3]">
                  {stats.sold_items.map((it, idx) => (
                    <div key={idx} className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-semibold flex-1 pr-3">{it.name}</div>
                        <div className="text-right">
                          <div className="text-[10px] uppercase text-[#9ca3af]">Units</div>
                          <div className="mono font-bold text-lg">{it.qty}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-[#f3f3f3]">
                        <div>
                          <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Revenue</div>
                          <div className="mono font-bold">{money(it.revenue)}</div>
                          <div className="text-[10px] mono mt-0.5">
                            <span className="text-green-700">{money(it.paid_revenue)}</span>
                            <span className="text-[#9ca3af]"> / </span>
                            <span className="text-[#d92027]">{money(it.unpaid_revenue)}</span>
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Profit</div>
                          <div className={`mono font-bold ${it.profit >= 0 ? '' : 'text-[#d92027]'}`}>{money(it.profit)}</div>
                          <div className="text-[10px] mono mt-0.5">
                            <span className="text-green-700">{money(it.paid_profit)}</span>
                            <span className="text-[#9ca3af]"> / </span>
                            <span className="text-[#d92027]">{money(it.unpaid_profit)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Block 2 — Past Due Alerts (collapsible)
   ============================================================ */
function PastDueAlerts() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);

  const load = () => adminApi.get('/admin/dashboard/past-due').then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  if (items === null) return <div className="card p-6 text-sm text-[#6b7280]">Loading past-due…</div>;

  const totalAmount = items.reduce((s, i) => s + i.balance, 0);

  if (items.length === 0) {
    return (
      <div className="card p-5 flex items-center gap-3 border-l-4 border-l-green-600" data-testid="past-due-block-empty">
        <div className="w-9 h-9 rounded-full bg-green-50 text-green-700 flex items-center justify-center font-bold">✓</div>
        <div>
          <div className="text-sm font-semibold text-green-700">No past-due invoices</div>
          <div className="text-xs text-[#6b7280]">Everyone is paid up</div>
        </div>
      </div>
    );
  }

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.invoice_id)));
  };

  const sendReminders = async () => {
    if (selected.size === 0) return toast.error('Select at least one invoice');
    setSending(true);
    try {
      const r = await adminApi.post('/admin/invoices/bulk-reminder', {
        invoice_ids: Array.from(selected),
      });
      toast.success(`Reminders sent: ${r.data.sent}${r.data.failed ? ` (${r.data.failed} failed)` : ''}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="card overflow-hidden border-l-4 border-l-[#d92027]" data-testid="past-due-block">
      <div onClick={() => setExpanded(!expanded)} className="p-5 sm:p-6 cursor-pointer hover:bg-[#fafafa]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#fde8e8] text-[#d92027] flex items-center justify-center">
              <AlertTriangle size={18} />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-semibold">
                You have <span className="text-[#d92027]">{items.length}</span> past-due invoice{items.length === 1 ? '' : 's'}
              </h2>
              <p className="text-xs text-[#6b7280]">
                Total outstanding: <span className="mono font-semibold">{money(totalAmount)}</span> · click to review &amp; remind
              </p>
            </div>
          </div>
          {expanded ? <ChevronUp size={20} className="text-[#6b7280]" /> : <ChevronDown size={20} className="text-[#6b7280]" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#f3f3f3] bg-[#fafafa]" data-testid="past-due-detail">
          <div className="p-4 flex items-center justify-between flex-wrap gap-2 border-b border-[#f3f3f3]">
            <label className="text-xs text-[#6b7280] flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={selected.size === items.length} onChange={toggleAll} />
              Select all
            </label>
            <button
              onClick={sendReminders}
              disabled={selected.size === 0 || sending}
              className="btn-brand text-sm flex items-center gap-2 disabled:opacity-40"
              data-testid="send-bulk-reminders-btn"
            >
              <Send size={14} /> {sending ? 'Sending…' : `Send ${selected.size || ''} reminder${selected.size === 1 ? '' : 's'}`.trim()}
            </button>
          </div>

          {/* Desktop table */}
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full text-sm">
              <thead className="bg-[#fafafa] text-[#6b7280] text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 w-10"></th>
                  <th className="text-left px-3 py-2">Invoice</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Due</th>
                  <th className="text-right px-3 py-2">Days Late</th>
                  <th className="text-right px-3 py-2">Balance</th>
                  <th className="text-left px-3 py-2">Last Reminder</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((inv) => (
                  <tr
                    key={inv.invoice_id}
                    className="border-t border-[#f3f3f3] bg-white hover:bg-[#fff5f5]"
                    data-testid={`past-due-row-${inv.invoice_id}`}
                  >
                    <td className="px-3 py-2 text-center">
                      <input type="checkbox" checked={selected.has(inv.invoice_id)} onChange={() => toggle(inv.invoice_id)} />
                    </td>
                    <td className="px-3 py-2 mono font-semibold">{inv.invoice_number}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{inv.customer_name}</div>
                      <div className="text-xs text-[#6b7280]">{inv.customer_email}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{fmtDate(inv.due_date)}</td>
                    <td className="text-right px-3 py-2 mono font-bold text-[#d92027]">{inv.days_overdue}d</td>
                    <td className="text-right px-3 py-2 mono font-semibold">{money(inv.balance)}</td>
                    <td className="px-3 py-2 text-xs text-[#6b7280]">
                      {inv.last_reminder_sent ? fmtDate(inv.last_reminder_sent) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => navigate(`/admin/orders/${inv.invoice_id}`)}
                        className="p-1.5 hover:bg-[#fafafa] rounded text-[#6b7280]"
                        title="Open"
                      >
                        <ExternalLink size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-[#f3f3f3]">
            {items.map((inv) => (
              <div key={inv.invoice_id} className="p-3 bg-white">
                <div className="flex items-start gap-3 mb-2">
                  <input
                    type="checkbox"
                    checked={selected.has(inv.invoice_id)}
                    onChange={() => toggle(inv.invoice_id)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="mono font-semibold truncate">{inv.invoice_number}</div>
                      <div className="text-xs font-bold text-[#d92027] mono">{inv.days_overdue}d late</div>
                    </div>
                    <div className="text-sm truncate">{inv.customer_name}</div>
                    <div className="text-xs text-[#6b7280] flex items-center justify-between mt-1">
                      <span>Due {fmtDate(inv.due_date)}</span>
                      <span className="mono font-semibold text-[#0a0a0a]">{money(inv.balance)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function InventoryValueCard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { adminApi.get('/admin/dashboard').then((r) => setD(r.data)); }, []);
  if (!d) return <div className="card p-6 text-sm text-[#6b7280]" data-testid="inventory-value-loading">Loading stock value…</div>;
  return (
    <div onClick={() => navigate('/admin/inventory')} className="card p-5 sm:p-6 cursor-pointer hover:bg-[#fafafa]" data-testid="inventory-value-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-full bg-[#fef2f2] text-[#d92027] flex items-center justify-center"><Package size={18} /></div>
        <div>
          <h2 className="text-base sm:text-lg font-semibold">Inventory on hand</h2>
          <p className="text-xs text-[#6b7280]">Valued at FIFO landed cost</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div data-testid="inventory-value" className="col-span-2 sm:col-span-1"><BigStat label="Stock value" value={money(d.inventory_value)} /></div>
        <div data-testid="inventory-units"><BigStat label="Units" value={(d.inventory_units || 0).toLocaleString()} /></div>
        <div data-testid="inventory-skus"><BigStat label="In stock" value={d.inventory_skus_in_stock} /></div>
      </div>
    </div>
  );
}

/* ============================================================
   Block 3 — Inventory Alerts (collapsible)
   ============================================================ */
function InventoryAlerts() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState(null);

  useEffect(() => {
    adminApi.get('/admin/dashboard/low-stock', { params: { threshold: 10 } }).then((r) => setItems(r.data || []));
  }, []);

  if (items === null) return <div className="card p-6 text-sm text-[#6b7280]">Loading inventory…</div>;

  if (items.length === 0) {
    return (
      <div className="card p-5 flex items-center gap-3 border-l-4 border-l-green-600" data-testid="inventory-block-empty">
        <div className="w-9 h-9 rounded-full bg-green-50 text-green-700 flex items-center justify-center">
          <Package size={18} />
        </div>
        <div>
          <div className="text-sm font-semibold text-green-700">All inventory is healthy</div>
          <div className="text-xs text-[#6b7280]">No products are running low</div>
        </div>
      </div>
    );
  }

  // Inline summary text: "Back Brace (5 left) · Knee Brace (8 left) ..."
  const top = items.slice(0, 3).map((p) => `${p.name} (${p.remaining_qty} left)`).join(' · ');
  const more = items.length > 3 ? ` · +${items.length - 3} more` : '';

  return (
    <div className="card overflow-hidden border-l-4 border-l-amber-600" data-testid="inventory-block">
      <div onClick={() => setExpanded(!expanded)} className="p-5 sm:p-6 cursor-pointer hover:bg-[#fafafa]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0">
              <Package size={18} />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold">
                You're going low on <span className="text-amber-700">{items.length}</span> product{items.length === 1 ? '' : 's'}
              </h2>
              <p className="text-xs text-[#6b7280] mt-0.5 truncate">{top}{more}</p>
            </div>
          </div>
          {expanded ? <ChevronUp size={20} className="text-[#6b7280]" /> : <ChevronDown size={20} className="text-[#6b7280]" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#f3f3f3] bg-[#fafafa] p-4" data-testid="inventory-detail">
          <div className="bg-white border border-[#eaeaea] rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead className="bg-[#fafafa] text-[#6b7280] text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2">Product</th>
                  <th className="text-right px-4 py-2">Ordered</th>
                  <th className="text-right px-4 py-2">Remaining</th>
                  <th className="text-right px-4 py-2">Net Cost</th>
                  <th className="text-right px-4 py-2">Price</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.product_id} className="border-t border-[#f3f3f3] hover:bg-[#fafafa]">
                    <td className="px-4 py-2 font-medium">{p.name}</td>
                    <td className="text-right px-4 py-2 mono text-[#6b7280]">{p.ordered_qty}</td>
                    <td className={`text-right px-4 py-2 mono font-bold ${p.remaining_qty === 0 ? 'text-[#d92027]' : 'text-amber-700'}`}>
                      {p.remaining_qty}
                    </td>
                    <td className="text-right px-4 py-2 mono text-[#6b7280]">{money(p.net_cost_per_unit)}</td>
                    <td className="text-right px-4 py-2 mono">{money(p.default_price)}</td>
                    <td className="text-right px-2 py-2">
                      <button
                        onClick={() => navigate('/admin/inventory')}
                        className="p-1.5 hover:bg-white rounded text-[#6b7280]"
                        title="Open inventory"
                      >
                        <ExternalLink size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Helpers
   ============================================================ */
function BigStat({ label, value, tint }) {
  const tints = {
    green: 'text-green-700',
    red: 'text-[#d92027]',
  };
  return (
    <div>
      <div className="text-[10px] sm:text-xs uppercase tracking-wider text-[#6b7280] font-semibold mb-1">{label}</div>
      <div className={`text-xl sm:text-3xl font-bold mono tracking-tight ${tints[tint] || ''}`}>{value}</div>
    </div>
  );
}

function SmallStat({ label, value, tint }) {
  const tints = {
    green: 'text-green-700',
    red: 'text-[#d92027]',
    blue: 'text-blue-700',
  };
  return (
    <div className="bg-white border border-[#eaeaea] rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold mb-0.5">{label}</div>
      <div className={`text-base sm:text-lg font-bold mono ${tints[tint] || ''}`}>{value}</div>
    </div>
  );
}

function ClickableStat({ label, value, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left bg-white border rounded-lg p-3 transition-all cursor-pointer ${
        active ? 'border-[#d92027] ring-2 ring-[#fde8e8]' : 'border-[#eaeaea] hover:border-[#d92027]'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold">{label}</div>
        {active ? <ChevronUp size={12} className="text-[#d92027]" /> : <ChevronDown size={12} className="text-[#9ca3af]" />}
      </div>
      <div className="text-base sm:text-lg font-bold mono mt-0.5 text-[#0a0a0a]">{value}</div>
      <div className="text-[10px] text-[#9ca3af] mt-0.5">{active ? 'click to hide list' : 'click to view list'}</div>
    </button>
  );
}

function StatusPill({ status }) {
  return <span className={statusPill(status)}>{statusLabel(status)}</span>;
}
